import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTickersAction } from "../actions";
import { instrumentSelector, selectedInstrumentSelector, selectedInstrumentSettingSelector, setSelectedInstrument } from "../store/slice/instrument";
import { companySettingsSelector } from '../store/slice/settings';
import { updateDefaultDateTime } from '@euroland/libs/utils/formatDate';

/**
 *
 * @returns {[{
 *  instruments: (import("../..").IInstrument)[],
 *  loading: boolean,
 *  error: object,
 * }, {
 *  getInstruments: () => void
 * }]}
 */
const useInstruments = () => {
  const instrumentsState = useSelector(instrumentSelector);
  const dispatch = useDispatch();
  // toCurrency: will implement when change the currency feature applied
  // adjusted close: will apply when required enabled adjust price
  const getInstruments = useCallback(() => dispatch(getTickersAction({toCurrency: null, adjClose: false})), [dispatch]);

  return [instrumentsState, { getInstruments }];
};

/**
 *
 * @returns {[{
 *  selectedInstrument: (import("../..").IInstrument),
 * }, {
 * onChangeSelectedInstrument: (id: (import("../..").IInstrument['instrumentId'])) => void
 * }]}}
 */
export const useInstrument = () => {
  const selectedInstrument = useSelector(selectedInstrumentSelector);
  const { instruments } = useSelector(instrumentSelector);
  const companySetting = useSelector(companySettingsSelector);
  const dispatch = useDispatch();
  const onChangeSelectedInstrument = useCallback(
    id => {
      const instrumentSetting = companySetting.instruments.find(x => Number(x.id) === Number(id));
      const selectedInst = instruments.find(inst => Number(inst.instrumentId) === Number(id));
      let timeZone = instrumentSetting?.customTimezone || selectedInst?.marketTimeZoneNameIANA || companySetting.timeZone;
      updateDefaultDateTime({ timezone: timeZone });
      dispatch(setSelectedInstrument(id));
    },
    [companySetting.instruments, companySetting.timeZone, dispatch, instruments]
  );
  return [{ selectedInstrument }, { onChangeSelectedInstrument }];
};


/**
 * Hook to get the timezone for the selected instrument.
 * Returns the timezone in the following priority order:
 * 1. Custom timezone from instrument settings
 * 2. Market timezone from selected instrument
 * 3. Company timezone setting
 * 
 * @returns {{ selectedInstrumentTimeZone: string }} Object containing the selected instrument's timezone
 */
export const useSelectedInstrumentTimeZone = () =>{
  const selectedInstrument = useSelector(selectedInstrumentSelector);
  const selectedInstrumentSetting = useSelector(selectedInstrumentSettingSelector);
  const companySetting = useSelector(companySettingsSelector);
  
  const selectedInstrumentTimeZone = useMemo(() => {
    return selectedInstrumentSetting?.customTimezone || selectedInstrument?.marketTimeZoneNameIANA || companySetting?.timeZone || companySetting?.timezone;
  }, [selectedInstrument?.marketTimeZoneNameIANA, selectedInstrumentSetting?.customTimezone, companySetting?.timeZone, companySetting?.timezone]);
  return { selectedInstrumentTimeZone };
}

export default useInstruments;
